import { cn, Logo } from '@syncanix/ui';

import type { ReactElement } from 'react';

/**
 * App-header brand cluster (SYN-31 — "header = brand mark (with glow) +
 * 'Syncanix' wordmark"). Renders the canonical Syncanix brand glyph — the
 * `@syncanix/ui` `Logo` badge — wrapped in a soft gradient glow, followed by
 * the gradient "Syncanix" wordmark.
 *
 * The glyph is always present (the brand presence shown alone on a narrow top
 * bar); the wordmark joins it once there is room (`sm`+). The glyph itself is
 * purely decorative — `Logo` is `aria-hidden` by default — so the accessible
 * "Syncanix" name is carried solely by the wordmark's `aria-label`.
 *
 * The glow is a STATIC blurred brand-gradient halo (no animation, no
 * transition), so `prefers-reduced-motion` has nothing to honour; it is
 * decorative (`aria-hidden`) and `pointer-events-none` so it never intercepts a
 * click. Spacing uses the flex `gap` (logical, RTL-correct) — no physical
 * directional margins.
 */
export interface BrandMarkProps {
  /**
   * Wordmark visibility. `'auto'` (default) hides it below `sm` — the top-bar
   * behaviour, where a 320–375px bar only has room for the glyph. `'always'`
   * keeps it visible at every width — used inside the mobile nav drawer, whose
   * header has the full drawer width to itself.
   */
  wordmark?: 'auto' | 'always';
}

export function BrandMark({ wordmark = 'auto' }: BrandMarkProps): ReactElement {
  return (
    // shrink-0 (not min-w-0): the brand cluster is a fixed-size anchor — letting
    // it shrink below the glyph's 28px made the logo paint outside its box on a
    // tight 375px bar. The flexible, truncating element beside it is the
    // workspace switcher.
    <span className="flex shrink-0 items-center gap-2">
      <span
        data-testid="app-brand-mark"
        aria-hidden="true"
        className="relative inline-flex shrink-0 items-center justify-center"
      >
        {/* Bedrock dropped the gradient blur halo that used to sit behind the
            mark. It is the brand gradient used as a GLOW — a fill by any other
            name — and principle 02 keeps the gradient to 2px indicators. The
            mark itself still carries it, which is the one sanctioned place:
            a logotype, where the gradient IS the identity rather than
            decoration applied to something else. */}
        <Logo variant="badge" size={28} className="relative" />
      </span>
      <span
        aria-label="Syncanix"
        className={cn(
          // Solid ink, not gradient-clipped text. Bedrock's own top bar draws
          // the wordmark in `ink` and lets the MARK beside it carry the colour
          // — which is also the only version whose contrast can be measured at
          // all: `bg-clip-text` paints the glyphs cyan→magenta, and the cyan
          // end is 1.9:1 on a white bar.
          'text-[15.5px] font-heading tracking-[-0.02em] text-foreground',
          wordmark === 'auto' && 'hidden sm:inline',
        )}
      >
        Syncanix
      </span>
    </span>
  );
}
